import type { EChartsOption, LineSeriesOption } from "echarts";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import {
  buildEtiquetasSemana,
  SemanaMonitoreo,
} from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import { leerResultado } from "../../../../../modulos/monitoreo/sesion/response/SesionDashboardResponse";
import {
  ESTADO_LABEL,
  evaluarEnLote,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { EChartRender } from "../../../../implementations/chart/EChartRender";
import {
  COLOR_SIN_REFERENCIA,
  ESTADO_COLOR,
} from "./monitoreoAnalisisPaleta";
import {
  formatNivel,
  formatPorcentaje,
} from "../../../../../modulos/monitoreo/sesion/formatIncidencia";

/** Patrones de línea que codifican la **plaga**, mientras el color codifica el
 *  **lote**. Es codificación compuesta deliberada: permite leer varias plagas
 *  sobre varios lotes sin darle un tono propio a cada combinación (que se
 *  saldría de la paleta validada), y sirve de encoding secundario para quien no
 *  distingue los tonos. */
const PATRONES_LINEA: (number[] | undefined)[] = [
  undefined, // sólida
  [8, 4],
  [3, 3],
  [12, 4, 3, 4],
  [6, 3, 6, 3],
  [15, 5],
];

/** Tope del eje Y cuando no hay ninguna combinación seleccionada. */
const Y_MAX_FALLBACK = 50;

/** Hasta cuántas series se etiquetan directamente sobre la curva. Por encima
 *  de esto las etiquetas se pisan entre sí y la leyenda alcanza. */
const MAX_SERIES_CON_ETIQUETA = 4;

/** Identidad de una serie, para reconstruir el estado del semáforo en el
 *  tooltip a partir del nombre que ECharts devuelve. */
type SerieMeta = { loteId: string; plagaId: string };

/** Gráfico de líneas del dashboard de monitoreo: una curva por combinación
 *  lote × plaga activa a lo largo de las sesiones (eje X = semanas ISO).
 *
 *  Es interactivo: el contenedor le pasa la selección de pills con
 *  `setSelection`, que re-aplica las opciones sobre la instancia viva
 *  (`refresh()` de la base) en vez de recrear el gráfico. */
export class MonitoreoAnalisisChartRender extends EChartRender {
  /**
   * @param _semanas     Histórico agrupado por semana ISO, ascendente.
   * @param _plagasById  Catálogo de plagas indexado por id.
   * @param _loteColores Color fijo por `loteId` (nunca depende de la selección).
   * @param _loteNombres Nombre por `loteId`, solo para mostrar.
   */
  constructor(
    private readonly _semanas: SemanaMonitoreo[],
    private readonly _plagasById: Map<string, MonitoreoPlagaEnfermedadItem>,
    private readonly _loteColores: Map<string, string>,
    private readonly _loteNombres: Map<string, string>,
  ) {
    super();
  }

  /** Cambia la selección de pills y repinta sobre la instancia viva.
   *  @param lotes Ids de lote activos (no nombres). */
  setSelection(lotes: string[], plagas: string[]): void {
    this._activeLotes = lotes;
    this._activePlagas = plagas;
    this.refresh();
  }

  protected getOption(): EChartsOption {
    const labels = buildEtiquetasSemana(this._semanas);
    const series = this._buildSeries();
    const plagaUnica = this._getPlagaUnicaConUmbral();

    // Bandas y líneas de umbral: solo con UNA plaga activa. Con varias, cada
    // una tiene su propio umbral y superponerlos confunde en vez de informar.
    if (plagaUnica && series.length > 0) {
      series[0].markArea = this._buildMarkArea(plagaUnica);
      series[0].markLine = this._buildMarkLine(plagaUnica);
    }

    return {
      grid: { left: 56, right: 96, top: 56, bottom: 32, containLabel: true },
      legend: {
        type: "scroll",
        top: 0,
        left: 0,
        itemWidth: 24,
        itemHeight: 3,
        textStyle: { fontSize: 12, color: "#5c5b56" },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        formatter: this._formatTooltip,
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLine: { lineStyle: { color: "#e4e3dc" } },
        axisTick: { show: false },
        axisLabel: { fontSize: 12, color: "#5c5b56" },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: this._getYMax(),
        name: "Conteo por árbol",
        nameTextStyle: { fontSize: 11, color: "#8c8a82", align: "left" },
        nameGap: 12,
        axisLabel: { fontSize: 11, color: "#8c8a82" },
        splitLine: { lineStyle: { color: "#e4e3dc", opacity: 0.5 } },
      },
      series,
    };
  }

  /** Lotes actualmente activos (los enciende el contenedor vía pills). */
  private _activeLotes: string[] = [];
  /** Plagas actualmente activas. */
  private _activePlagas: string[] = [];
  /** Nombre de serie → lote/plaga, para el tooltip. Se rearma en cada
   *  `_buildSeries` porque los nombres cambian con la selección (con una sola
   *  plaga activa la serie se llama solo por el lote). */
  private _seriesMeta = new Map<string, SerieMeta>();

  /** Una serie por combinación lote × plaga con al menos un dato. El color sale
   *  del lote (fijo) y el patrón de línea de la posición de la plaga dentro de
   *  la selección activa. */
  private _buildSeries(): LineSeriesOption[] {
    this._seriesMeta.clear();
    const series: LineSeriesOption[] = [];
    const plagaUnicaActiva = this._activePlagas.length === 1;
    const unicaCombinacion =
      plagaUnicaActiva && this._activeLotes.length === 1;

    for (const loteId of this._activeLotes) {
      const color = this._loteColores.get(loteId);
      // Lote sin slot de paleta (finca con más lotes que colores): no se
      // dibuja. La pill correspondiente ya se muestra deshabilitada.
      if (!color) continue;
      const loteNombre = this._loteNombres.get(loteId) ?? loteId;

      this._activePlagas.forEach((plagaId, indice) => {
        const plaga = this._plagasById.get(plagaId);
        if (!plaga) return;

        // `null` solo donde el lote no se monitoreó; una semana monitoreada sin
        // detección vale 0 y se dibuja sobre el eje.
        //
        // El valor va **crudo**, sin redondear: hay cultivos —papa— con
        // umbrales por debajo de 0.1, y cuantizarlo acá movía el punto respecto
        // de su banda de umbral. No hace falta redondear para que se lea bien:
        // el eje calcula sus ticks desde la escala, no desde el dato, y el
        // tooltip formatea con `formatIncidencia`.
        const data = this._semanas.map((semana) => {
          const lote = leerResultado(semana, loteId);
          if (lote === null) return null;
          return evaluarEnLote(lote, plagaId, plaga)?.incidencia ?? null;
        });
        if (data.every((valor) => valor === null)) return;

        // Con una sola plaga activa, el nombre de la plaga es redundante en
        // cada serie (ya está en el subtítulo del gráfico).
        const nombre = plagaUnicaActiva
          ? loteNombre
          : `${loteNombre} · ${plaga.nombreComun}`;
        this._seriesMeta.set(nombre, { loteId, plagaId });

        series.push({
          type: "line",
          name: nombre,
          data,
          connectNulls: true,
          smooth: 0.35,
          // Punto sólido con anillo blanco: se despega de las bandas de
          // umbral, que el anillo hueco por defecto de ECharts deja ver a
          // través y confunde con el fondo.
          symbol: "circle",
          symbolSize: 8,
          itemStyle: { color, borderColor: "#fff", borderWidth: 2 },
          lineStyle: {
            color,
            width: 2,
            type: PATRONES_LINEA[indice % PATRONES_LINEA.length],
          },
          // Relleno bajo la curva SOLO con una combinación: es lo que deja
          // leer de un vistazo el recorrido de una plaga en un lote contra sus
          // umbrales. Con varias series los rellenos se superponen y tapan las
          // bandas, así que ahí se dibuja solo la línea.
          //
          // Va en degradado y no en color plano: un relleno parejo desde el eje
          // tiñe las bandas de umbral que tiene debajo —el verde de abajo se
          // vuelve del color del lote— y se pierde justo la referencia contra
          // la que hay que leer la curva. Así el peso queda pegado a la línea y
          // las bandas siguen visibles al fondo.
          areaStyle: unicaCombinacion
            ? {
                color: {
                  type: "linear",
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: `${color}40` },
                    { offset: 1, color: `${color}00` },
                  ],
                },
              }
            : undefined,
        });
      });
    }

    // Etiqueta directa sobre el extremo de la curva mientras sean pocas: la
    // paleta tiene tonos por debajo de 3:1 contra el blanco, y la guía exige
    // que la identidad no dependa solo del color.
    if (series.length <= MAX_SERIES_CON_ETIQUETA) {
      for (const serie of series) {
        serie.endLabel = {
          show: true,
          formatter: "{a}",
          fontSize: 12,
          color: "#5c5b56",
          distance: 6,
        };
      }
    }

    return series;
  }

  /** La plaga activa cuando hay exactamente una y tiene umbrales que dibujar.
   *  Una plaga crítica no los tiene (cualquier detección es alerta), así que
   *  para ella no se pintan bandas. */
  private _getPlagaUnicaConUmbral(): MonitoreoPlagaEnfermedadItem | null {
    if (this._activePlagas.length !== 1) return null;
    const plaga = this._plagasById.get(this._activePlagas[0]);
    if (!plaga || plaga.isCritico) return null;
    return plaga;
  }

  /**
   * Umbrales de incidencia con los que se dibujan las franjas y las líneas.
   *
   * Salen de la referencia de **la semana más reciente del rango**, que es la
   * aproximación más cercana a "la versión vigente hoy" que pide la sección 11.3
   * con lo que la web tiene hoy: los valores vigentes de verdad son los de la
   * tabla por cultivo, y esa lista llega con `listMonitoreoCultivoPlagas`
   * (sección 6.2), que todavía no existe. Cuando exista, esto lee de ahí.
   *
   * No se usan `rangoBajo`/`rangoMedio` del catálogo: son umbrales de **nivel**
   * (individuos o ácaros por lupada), no de incidencia, y dibujarlos sobre un
   * eje de porcentaje de árboles es lo que venía pasando — con los ácaros, 0,2
   * sobre un eje que llega a 46.
   */
  private _getUmbralesIncidencia(
    plagaId: string,
  ): { bajo: number; medio: number } | null {
    for (let i = this._semanas.length - 1; i >= 0; i -= 1) {
      for (const loteId of this._activeLotes) {
        const referencia = leerResultado(this._semanas[i], loteId)?.referencias[
          plagaId
        ];
        const bajo = referencia?.incidenciaBajoMax;
        const medio = referencia?.incidenciaMedioMax;
        if (bajo !== undefined && medio !== undefined && medio > 0) {
          return { bajo, medio };
        }
      }
    }
    return null;
  }

  /**
   * Aviso de la sección 11.3: si alguna semana del rango se evaluó con otra
   * versión de las referencias, el gráfico tiene que decirlo.
   *
   * Sin esto, dos puntos con el mismo color pueden venir de umbrales distintos y
   * nada en pantalla lo delata — que es la forma más silenciosa de leer mal una
   * serie histórica.
   */
  getAvisoVersiones(): string | null {
    const versiones = new Set<number>();
    for (const semana of this._semanas) {
      for (const loteId of this._activeLotes) {
        const lote = leerResultado(semana, loteId);
        if (!lote) continue;
        for (const plagaId of this._activePlagas) {
          const version = lote.referencias[plagaId]?.referenciaVersion;
          if (version !== undefined) versiones.add(version);
        }
      }
    }
    if (versiones.size <= 1) return null;
    const ordenadas = [...versiones].sort((a, b) => a - b);
    return `Los valores de referencia cambiaron en el rango (versión ${ordenadas[0]} → ${ordenadas[ordenadas.length - 1]}): las semanas se evaluaron con los valores vigentes en su momento.`;
  }

  /** Bandas verde/ámbar/rojo calibradas al umbral de la plaga activa. */
  private _buildMarkArea(
    plaga: MonitoreoPlagaEnfermedadItem,
  ): LineSeriesOption["markArea"] {
    const umbrales = this._getUmbralesIncidencia(plaga.id);
    if (!umbrales) return undefined;
    return {
      silent: true,
      data: [
        [
          { yAxis: 0, itemStyle: { color: "rgba(22, 163, 74, 0.06)" } },
          { yAxis: umbrales.bajo },
        ],
        [
          {
            yAxis: umbrales.bajo,
            itemStyle: { color: "rgba(217, 119, 6, 0.07)" },
          },
          { yAxis: umbrales.medio },
        ],
        [
          {
            yAxis: umbrales.medio,
            itemStyle: { color: "rgba(220, 38, 38, 0.06)" },
          },
          // Sin `yAxis` de cierre: la banda roja llega hasta el tope del eje.
          { yAxis: "max" },
        ],
      ],
    } as LineSeriesOption["markArea"];
  }

  /** Líneas punteadas de referencia sobre los dos umbrales. */
  private _buildMarkLine(
    plaga: MonitoreoPlagaEnfermedadItem,
  ): LineSeriesOption["markLine"] {
    const umbrales = this._getUmbralesIncidencia(plaga.id);
    if (!umbrales) return undefined;
    // Sin etiqueta sobre la línea: chocaba con el nombre de la serie que se
    // dibuja en su extremo derecho. Los valores ya están en el subtítulo del
    // gráfico y la leyenda del encabezado dice cuál es cuál.
    const linea = (valor: number, color: string) => ({
      yAxis: valor,
      lineStyle: { color, width: 1.5, type: "dashed" as const },
      label: { show: false },
    });
    return {
      silent: true,
      symbol: "none",
      data: [
        linea(umbrales.bajo, ESTADO_COLOR.warn),
        linea(umbrales.medio, ESTADO_COLOR.alert),
      ],
    } as LineSeriesOption["markLine"];
  }

  /** Tope del eje Y: el mayor valor activo con 25% de aire. Con una sola plaga
   *  se considera además su umbral de alerta, para que la línea de referencia
   *  no quede fuera de cuadro cuando todos los valores están por debajo. */
  private _getYMax(): number {
    let max = 0;
    for (const loteId of this._activeLotes) {
      for (const plagaId of this._activePlagas) {
        for (const semana of this._semanas) {
          const lote = leerResultado(semana, loteId);
          if (!lote) continue;
          const valor = evaluarEnLote(lote, plagaId, lote ? this._plagasById.get(plagaId) : undefined)?.incidencia ?? 0;
          if (valor > max) max = valor;
        }
      }
    }

    const plagaUnica = this._getPlagaUnicaConUmbral();
    if (plagaUnica) {
      const umbrales = this._getUmbralesIncidencia(plagaUnica.id);
      if (umbrales && umbrales.medio > max) max = umbrales.medio;
    }

    // Tope en 100: el eje es un porcentaje de árboles, así que un 25% de aire
    // sobre un 90% daría un eje hasta 112, que no existe.
    return Math.min(Math.ceil(max * 1.25) || Y_MAX_FALLBACK, 100);
  }

  /** Tooltip del eje: una fila por serie con su valor y el estado del semáforo
   *  en texto (nunca solo por color). Se declara como propiedad-flecha porque
   *  ECharts lo invoca sin ligar `this`. */
  private _formatTooltip = (params: unknown): string => {
    const items = Array.isArray(params) ? params : [params];
    if (items.length === 0) return "";

    const titulo = (items[0] as { axisValue?: string }).axisValue ?? "";
    const filas = items
      .map((item) => {
        const { seriesName, value, marker, dataIndex } = item as {
          seriesName?: string;
          value?: number | null;
          marker?: string;
          dataIndex?: number;
        };
        if (value === null || value === undefined || !seriesName) return null;

        const meta = this._seriesMeta.get(seriesName);
        const plaga = meta ? this._plagasById.get(meta.plagaId) : undefined;

        // El estado sale de la referencia congelada de ESA semana, no de los
        // umbrales de hoy: `dataIndex` es la posición en `data`, que se armó
        // recorriendo `this._semanas` en orden.
        const semana = this._semanas[dataIndex ?? -1];
        const lote = meta && semana ? leerResultado(semana, meta.loteId) : null;
        const evaluacion =
          lote && meta ? evaluarEnLote(lote, meta.plagaId, plaga) : null;

        const nivel = evaluacion ? ` · ${formatNivel(evaluacion)}` : "";
        if (!evaluacion || evaluacion.estado === null) {
          // Benéfico o sin valores de referencia: el número se muestra, el
          // estado no se inventa.
          return `<div>${marker ?? ""}${seriesName}: <strong>${formatPorcentaje(value)}</strong>${nivel} <span style="color:${COLOR_SIN_REFERENCIA}">· sin valores de referencia</span></div>`;
        }
        const color = ESTADO_COLOR[evaluacion.estado];
        const label = ESTADO_LABEL[evaluacion.estado];
        return `<div>${marker ?? ""}${seriesName}: <strong>${formatPorcentaje(value)}</strong>${nivel} <span style="color:${color}">· ${label}</span></div>`;
      })
      .filter((fila): fila is string => fila !== null);

    if (filas.length === 0) return "";
    return `<div style="font-weight:600;margin-bottom:4px">${titulo}</div>${filas.join("")}`;
  };
}
